import type { FaqSection } from "../types/faq.types";

export const FAQ_SECTIONS: FaqSection[] = [
  {
    id: "getting-started",
    label: "Getting started",
    items: [
      {
        question: "What is ItsHealer?",
        answer:
          "ItsHealer is a mental wellness platform that connects you with licensed therapists, counsellors, and relationship experts. Whether you're navigating relationship challenges, emotional distress, or just need someone to talk to — we match you with the right professional based on your unique needs.",
        tags: ["Introduction", "Mental Wellness", "Overview"],
      },
      {
        question: "Who can use ItsHealer?",
        answer:
          "Anyone 18 years or older seeking mental and emotional wellness support. Whether you're an individual, part of a couple, or looking for premarital guidance — ItsHealer is designed for you. We support both English and Hindi-speaking users.",
        tags: ["Eligibility", "Languages", "Support"],
      },
      {
        question: "How do I sign up?",
        answer:
          "It's quick and easy. Visit our homepage and click 'Get Started.' You'll create an account, complete a short onboarding questionnaire about your concerns and preferences, and we'll match you with a suitable healer. The entire process takes about 5–10 minutes.",
        tags: ["Onboarding", "Registration", "Account"],
      },
      {
        question: "Is ItsHealer a replacement for emergency services?",
        answer:
          "No. ItsHealer is not an emergency or crisis service. If you or someone you know is in immediate danger, please contact local emergency services or a crisis helpline. We do screen for safety during onboarding and can help connect you with urgent resources.",
        tags: ["Crisis Care", "Safety", "Help Lines"],
      },
      {
        question: "How is ItsHealer different from other therapy platforms?",
        answer:
          "We focus specifically on relationship and emotional wellness. Our matching system considers your specific concerns, preferred language, session type preferences, and emotional state to pair you with the most suitable healer. Every professional on our platform is verified through a rigorous onboarding process.",
        tags: ["Therapy", "Matching System", "Verification"],
      },
    ],
  },
  {
    id: "sessions",
    label: "Sessions & booking",
    items: [
      {
        question: "How do I book a session?",
        answer:
          "After completing your onboarding, you'll be matched with recommended healers. You can view their profiles, availability, and book a session directly through your dashboard. Choose between chat, audio, or video sessions based on your comfort level.",
        tags: ["Booking", "Scheduling", "Dashboard"],
      },
      {
        question: "What types of sessions are available?",
        answer:
          "We offer three session formats: Chat-first (text-based), Audio calls, and Video sessions. During onboarding, you can indicate your preference, and we'll match you accordingly. You can always switch formats as you become more comfortable.",
        tags: ["Formats", "Video & Audio", "Chat Support"],
      },
      {
        question: "How long is each session?",
        answer:
          "Standard sessions are 50 minutes. Some healers may offer shorter introductory sessions or extended sessions depending on the type of support you need. Session duration and pricing details are visible on each healer's profile.",
        tags: ["Duration", "Timing", "Introduction"],
      },
      {
        question: "Can I reschedule or cancel a session?",
        answer:
          "Yes. You can reschedule or cancel a session up to 24 hours before the scheduled time without any charge. Cancellations within 24 hours may be subject to a cancellation fee, depending on the healer's policy.",
        tags: ["Cancellation", "Rescheduling", "Refunds"],
      },
      {
        question: "What if I miss a session?",
        answer:
          "If you miss a scheduled session without prior notice, it will be marked as a no-show. Most healers have a no-show policy outlined in their profile. We recommend rescheduling at least 24 hours in advance to avoid any fees.",
        tags: ["Policies", "Fees", "No-Show"],
      },
    ],
  },
  {
    id: "healers",
    label: "Our healers",
    items: [
      {
        question: "Who are the healers on ItsHealer?",
        answer:
          "Our healers are licensed psychologists, clinical psychologists, psychiatrists, and certified counsellors. Each healer goes through a comprehensive verification process including credential checks, document validation, and professional background review before being listed.",
        tags: ["Our Healers", "Therapists", "Experts"],
      },
      {
        question: "How are healers verified?",
        answer:
          "Every healer completes a multi-step onboarding that includes identity verification, professional license validation, educational credential checks, registration authority verification, and declarations of ethical compliance. We take verification seriously to ensure your safety.",
        tags: ["Verification", "Safety Standards", "Credentials"],
      },
      {
        question: "Can I choose my own healer?",
        answer:
          "Absolutely. While we recommend healers based on your concerns and preferences, you have full control to browse profiles, read bios, and choose the professional you feel most comfortable with.",
        tags: ["Matching", "Selection", "Autonomy"],
      },
      {
        question: "What if I want to switch my healer?",
        answer:
          "You can switch healers at any time. We understand that the right therapeutic relationship is essential. Simply browse other available healers from your dashboard and book with someone new — no questions asked.",
        tags: ["Flexibility", "Therapist Change", "Matching"],
      },
    ],
  },
  {
    id: "onboarding",
    label: "Onboarding process",
    items: [
      {
        question: "How does the onboarding process work?",
        answer:
          "Our onboarding is a guided, step-by-step process. You'll share basic profile information, describe your relationship concerns, indicate your emotional state, set session preferences, and complete a safety check. The entire process takes about 5–10 minutes and helps us match you with the right healer.",
        tags: ["Onboarding Steps", "Duration", "Matching"],
      },
      {
        question: "Can I save my onboarding progress and come back later?",
        answer:
          "Yes! Your progress is automatically saved as a draft. You can close the browser, come back anytime, and pick up exactly where you left off. No need to start over.",
        tags: ["Draft Save", "Progress", "Autosave"],
      },
      {
        question: "What if I'm not sure about my concerns?",
        answer:
          "That's completely okay. Many people aren't sure exactly what they're dealing with — and that's one of the reasons to seek support. Select what feels closest to your situation, and your healer will help you explore and understand your feelings more deeply.",
        tags: ["Symptom Check", "Guidance", "Undecided"],
      },
      {
        question: "Can my partner also join ItsHealer?",
        answer:
          "Yes. During onboarding, you can indicate whether your partner is also joining. This helps us coordinate and provide better couple-focused support if needed. Each partner creates their own account for privacy.",
        tags: ["Couple Support", "Accounts", "Joint Therapy"],
      },
      {
        question: "What languages are supported?",
        answer:
          "ItsHealer currently supports English and Hindi for the onboarding flow, with more languages planned. You can also indicate your preferred language during onboarding, and we'll prioritize matching you with a healer who speaks it.",
        tags: ["Languages", "English & Hindi", "Matching"],
      },
    ],
  },
  {
    id: "privacy",
    label: "Privacy & safety",
    items: [
      {
        question: "Is my information kept confidential?",
        answer:
          "Yes. Your privacy is our top priority. All personal information, session details, and communication are encrypted and stored securely. We follow strict data protection standards and never share your information with third parties without your explicit consent.",
        tags: ["Privacy", "Encryption", "Security"],
      },
      {
        question: "Who can see my onboarding details?",
        answer:
          "Your onboarding information is used solely for therapist matching and care support. Only the healer you're matched with can see relevant details to provide better support. Administrative staff have limited access for platform operations only.",
        tags: ["Access Control", "Therapist Only", "Confidentiality"],
      },
      {
        question: "What happens if I report feeling unsafe?",
        answer:
          "If you indicate safety concerns during onboarding or at any point, our system flags this for priority attention. We provide immediate resources and can connect you with emergency support services. Your safety always comes first.",
        tags: ["Safety Alert", "Crisis Support", "Monitoring"],
      },
      {
        question: "Are sessions recorded?",
        answer:
          "No. Sessions are never recorded by ItsHealer. What happens in your session stays between you and your healer, maintaining the confidentiality that is fundamental to the therapeutic process.",
        tags: ["Confidentiality", "No Recording", "Trust"],
      },
      {
        question: "Can I delete my data?",
        answer:
          "Yes. Your health data is yours to control, and you can request deletion at any time by contacting our support team. Note: deletion is permanent, and we won't be able to recover the data once it's gone.",
        tags: ["Data Erasure", "Privacy Policy", "Control"],
      },
    ],
  },
  {
    id: "billing",
    label: "Billing & pricing",
    items: [
      {
        question: "How much does a session cost?",
        answer:
          "Session pricing varies by healer, session type, and duration. You can view each healer's pricing on their profile before booking. We strive to keep therapy accessible and offer various options across different price points.",
        tags: ["Pricing", "Therapy Rates", "Costs"],
      },
      {
        question: "What payment methods are accepted?",
        answer:
          "We accept major credit and debit cards, UPI, and net banking. All payments are processed securely through our payment partner. You'll receive a receipt for every transaction.",
        tags: ["Payments", "UPI & Netbanking", "Transactions"],
      },
      {
        question: "Is there a refund policy?",
        answer:
          "If a session doesn't take place due to a healer's unavailability or technical issues on our end, you'll receive a full refund. For other situations, our support team evaluates requests on a case-by-case basis to ensure fairness.",
        tags: ["Refund Policy", "Platform Issues", "Cancellations"],
      },
    ],
  },
  {
    id: "support",
    label: "Getting help",
    items: [
      {
        question: "How can I contact support?",
        answer:
          "You can reach our support team through the help section in your dashboard, or email us directly at support@itshealer.com. We typically respond within 24 hours on business days.",
        tags: ["Contact Us", "Support Response", "Helpdesk"],
      },
      {
        question: "What if I have a complaint about a healer?",
        answer:
          "We take every complaint seriously. You can report concerns through your dashboard or contact our support team directly. All reports are investigated thoroughly, and appropriate action is taken to maintain the quality and safety of our platform.",
        tags: ["Quality Standards", "Safety Reporting", "Complaints"],
      },
      {
        question: "Is there a community or support group?",
        answer:
          "We're building community features to help people connect with others on similar journeys. In the meantime, your healer can recommend support groups or community resources relevant to your situation.",
        tags: ["Support Groups", "Community", "Social Connect"],
      },
    ],
  },
];

export const HEALER_LOGO_URL =
  "https://res.cloudinary.com/dsalsyu0z/image/upload/v1771236641/Frame_2_njt372.png";
